import { useEffect, useRef, useState, type PointerEvent } from "react"
import { Link } from "react-router"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu"
import { SidebarMenuButton, SidebarMenuItem } from "@components/ui/sidebar"
import type { NavGroupItem } from "./navItems"

// Gives the pointer time to cross the gap between the trigger and the menu
const CLOSE_DELAY_MS = 150

const isMouse = (event: PointerEvent) => event.pointerType === "mouse"

export const NavDropdown = ({
  item,
  isActive,
}: {
  item: NavGroupItem
  isActive: boolean
}) => {
  const [open, setOpen] = useState(false)
  const closeTimeout = useRef<number>(undefined)
  const triggerRef = useRef<HTMLElement | null>(null)

  const cancelClose = () => window.clearTimeout(closeTimeout.current)
  const openMenu = () => {
    cancelClose()
    setOpen(true)
  }
  const scheduleClose = () => {
    cancelClose()
    closeTimeout.current = window.setTimeout(
      () => setOpen(false),
      CLOSE_DELAY_MS,
    )
  }

  useEffect(() => cancelClose, [])

  return (
    <SidebarMenuItem>
      <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
        {/* The trigger wraps the button's DOM element because React 18 can't pass refs to SidebarMenuButton */}
        <SidebarMenuButton
          asChild
          isActive={isActive}
          className="tw:data-[state=open]:bg-sidebar-accent tw:data-[state=open]:text-sidebar-accent-foreground"
        >
          <DropdownMenuTrigger
            aria-label={item.title}
            onPointerEnter={(event) => {
              triggerRef.current = event.currentTarget
              if (isMouse(event)) openMenu()
            }}
            onPointerLeave={(event) => isMouse(event) && scheduleClose()}
            // Hovering already opened the menu, so a mouse click shouldn't toggle it closed
            onPointerDown={(event) => isMouse(event) && event.preventDefault()}
          >
            <item.icon />
            <span>{item.title}</span>
          </DropdownMenuTrigger>
        </SidebarMenuButton>
        <DropdownMenuContent
          side="right"
          align="start"
          className="tw:min-w-56 tw:rounded-lg"
          onPointerEnter={(event) => isMouse(event) && cancelClose()}
          onPointerLeave={(event) => isMouse(event) && scheduleClose()}
          // Clicking the trigger counts as outside the menu, which would close what hovering opened
          onInteractOutside={(event) => {
            if (triggerRef.current?.contains(event.target as Node)) {
              event.preventDefault()
            }
          }}
        >
          <DropdownMenuLabel className="tw:text-xs tw:text-muted-foreground">
            {item.title}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {item.items.map((subItem) => (
            <DropdownMenuItem
              key={subItem.title}
              asChild
              className="tw:cursor-pointer tw:focus:bg-sidebar-accent tw:focus:text-sidebar-accent-foreground"
            >
              <Link
                to={subItem.href}
                className="tw:text-popover-foreground tw:no-underline"
              >
                {subItem.title}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  )
}
